import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Pressable, ScrollView, TextInput, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {useCourseJob, useCreateCourse} from '../../../Hooks/LmsCourseApi/useCourseList';

import CourseArtwork from './CourseArtwork';
import CourseButton from './CourseButton';
import MasterclassPicker from './MasterclassPicker';
import UploadCourseCard from './UploadCourseCard';
import {isSettled} from '../courseStatus';
import {useIngestCourse} from '../../../Hooks/LmsCourseApi/useCourseUpload';
import {
  describeCreateError,
  useCreateActivePuzzleCourse,
} from '../../../Hooks/LmsCourseApi/useActivePuzzles';
import {Masterclass} from '../../../Hooks/LmsCourseApi/useMasterclasses';
import {CourseJob} from '../../../Hooks/LmsCourseApi/types';
import {CARD_SHADOW, F, STATUS} from '../courseTheme';
import {prefersReducedMotion} from './prefersReducedMotion';

type Props = {
  onBack: () => void;
  onOpen: (uuid: string) => void;
};

const STATUS_COPY: Record<string, string> = {
  // 'pending' means queued and not yet collected by a worker — the pipeline
  // marks itself running before it starts downloading. Sitting here for more
  // than a minute or two usually means no worker is consuming the queue.
  pending: 'Queued, waiting for a worker to pick it up.',
  running:
    'Working. The recording is being downloaded, sampled for chessboards and ' +
    'analysed by the engine. A full masterclass can take upwards of an hour — ' +
    'you can leave this page and come back.',
  done: 'Done.',
  failed: 'This run failed.',
  cancelled: 'Stopped: the course was deleted while it was being built.',
};

/**
 * A course from launched ("active") puzzles reads no boards off the video —
 * the positions come from the classroom tool — so the detection sentence
 * would be wrong about what is happening for the next hour.
 */
const ACTIVE_RUNNING_COPY =
  'Working. The recording is being downloaded and transcribed, and the ' +
  'puzzles the coach launched are being checked by the engine and placed ' +
  'in the video. You can leave this page and come back.';

/**
 * Start a course generation from an S3 link and watch it run.
 *
 * Generation is far too slow to hold an HTTP request open, so the server
 * dispatches it to Celery and this screen polls the job. The recording is
 * identified by its link because that is what an admin has to hand — the
 * server resolves it back to a cc_video_courses row.
 */
/**
 * Which recording this screen is following, remembered across a reload.
 *
 * The run itself lives in Celery and is entirely indifferent to the browser —
 * but the progress card was plain component state, so a refresh during a
 * two-hour generation left the screen looking as though nothing had been
 * started. That is the moment someone presses Generate a second time.
 *
 * Wrapped because storage throws in private windows and when site data is
 * blocked; losing the card is a small thing and must never take the screen
 * down with it.
 */
const WATCHING_KEY = 'cc.masterclass.watching';

/**
 * Every card on this screen shares one centred column. Each used to carry its
 * own cap (820 for the picker, 720 for the rest), so on a wide screen they
 * stopped at different edges and hugged the left — it read as broken. 880
 * fits three recording tiles a row.
 */
const COLUMN = {width: '100%', maxWidth: 880, alignSelf: 'center'} as const;

const rememberWatching = (id: number | null) => {
  try {
    if (id == null) window.localStorage.removeItem(WATCHING_KEY);
    else window.localStorage.setItem(WATCHING_KEY, String(id));
  } catch {
    /* no storage: the card simply will not survive a reload */
  }
};

const recallWatching = (): number | null => {
  try {
    const raw = window.localStorage.getItem(WATCHING_KEY);
    const id = raw == null ? NaN : Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

export default function CreateCourseScreen({onBack, onOpen}: Props) {
  const [url, setUrl] = useState('');
  const [watching, setWatching] = useState<number | null>(() => recallWatching());
  // Anything the ingest endpoint wants the uploader to know that is not an
  // error — which parts are being transcribed, which transcript was unusable.
  const [uploadNote, setUploadNote] = useState('');
  // The S3-link form is the escape hatch, not the way in. Hidden by default
  // so the picker is the obvious path and nobody hand-types a bucket path
  // they could have selected.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);

  const ingest = useIngestCourse();

  const create = useCreateCourse();
  const createActive = useCreateActivePuzzleCourse();
  const [activeRetryError, setActiveRetryError] = useState<string | null>(null);
  const job = useCourseJob(watching);

  // The job an active-puzzle create answered with, shown until the first
  // poll lands — so the card appears the moment the sheet closes rather than
  // a poll later. Only for the recording being watched, never a stale one.
  const [startedJob, setStartedJob] = useState<CourseJob | null>(null);
  const startedNow =
    startedJob && startedJob.video_course_id === watching ? startedJob : null;

  const current = job.data ?? startedNow ?? create.data ?? null;
  // From the server once it says so (`puzzle_source`), or from this session
  // for a run started here before the first poll lands.
  const isActiveRun =
    (current as any)?.puzzle_source === 'launched' ||
    (startedJob != null && startedJob.video_course_id === watching);

  // The card sits below the picker; after the sheet closes it would be off
  // screen, and a build nobody can see starting looks like a click that did
  // nothing.
  const scrollRef = useRef<any>(null);
  const [revealJob, setRevealJob] = useState(0);
  useEffect(() => {
    if (!revealJob) return;
    scrollRef.current?.scrollToEnd?.({animated: !prefersReducedMotion()});
  }, [revealJob]);

  /** Same hand-over as every other create: watch the job, survive a reload. */
  const followActive = (started: CourseJob) => {
    setStartedAt(Date.now());
    setUploadNote('');
    setStartedJob(started);
    setWatching(started.video_course_id);
    rememberWatching(started.video_course_id);
    setRevealJob(n => n + 1);
  };

  /**
   * Retry a failed run the way it was started. An active-puzzle course goes
   * back through its own endpoint: the generic create would run board
   * detection on a recording whose puzzles never came from the video.
   */
  const retry = async (videoCourseId: number) => {
    setStartedAt(Date.now());
    if (!isActiveRun) {
      create.mutate({video_course_id: videoCourseId, force: true});
      return;
    }
    setActiveRetryError(null);
    try {
      followActive(
        await createActive.mutateAsync({
          video_course_id: videoCourseId,
          force: true,
        }),
      );
    } catch (error) {
      setActiveRetryError(describeCreateError(error).message);
    }
  };
  // `String(error)` would show an admin "Error: Request failed with status code
  // 403". The server's own message is the useful half; anything else gets a
  // sentence rather than a stack-trace fragment.
  const serverError = create.error
    ? (create.error as any)?.response?.data?.error ??
      'Something went wrong starting the generation. Try again.'
    : null;

  // Elapsed time, so a long run reads as progress rather than a hang.
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const running = current?.status === 'pending' || current?.status === 'running';

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Stop following a run that has finished, so the next visit to this screen
  // opens clean rather than on somebody's week-old result.
  useEffect(() => {
    if (isSettled(current?.status)) {
      rememberWatching(null);
    }
  }, [current?.status]);

  const elapsedLabel = (() => {
    if (!running || startedAt == null) return '';
    const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
    const mins = Math.floor(secs / 60);
    return mins ? `${mins}m ${secs % 60}s elapsed` : `${secs}s elapsed`;
  })();

  /**
   * Build a course from recordings picked out of the catalogue.
   *
   * They are sent by id, not by S3 key: a recording's object lives under its
   * own category folder, which the ingest endpoint refuses as a key, and
   * rightly — a key stored against the upload folder would be rebuilt
   * pointing at nothing. The id says "use the row that already describes
   * this file".
   */
  const buildFromPicked = async (
    picked: Masterclass[],
    chosenTitle: string,
    force = false,
  ) => {
    if (!picked.length) return;
    setPickError(null);
    setStartedAt(Date.now());

    try {
      const job = await ingest.mutateAsync({
        // Only what the admin typed: the server names the course from its
        // chapters otherwise. Sending the first recording's title here froze
        // it in place as though an admin had chosen it.
        title: chosenTitle.trim(),
        force,
        parts: picked.map(item => ({
          video_course_id: item.video_course_id,
          title: item.title,
        })),
      });
      setUploadNote('');
      setWatching(job.video_course_id);
      rememberWatching(job.video_course_id);
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.error;
      if (status === 409) {
        // The first recording already anchors a course. Say so, and say what
        // the options are — the server will not overwrite it without being
        // told to.
        setPickError(
          `${detail ?? 'A course already exists for the first recording.'} ` +
            `Reorder the list to start from a different recording, or open ` +
            `the existing course from the Masterclass list.`,
        );
        return;
      }
      setPickError(detail ?? 'Could not start the course. Try again.');
    }
  };

  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setStartedAt(Date.now());
    create.mutate(
      {video_url: trimmed},
      {
        onSuccess: data => {
          setWatching(data.video_course_id);
          rememberWatching(data.video_course_id);
        },
      },
    );
  };

  return (
    <View style={{flex: 1, backgroundColor: F.teal}}>
      <View
        style={{
          ...COLUMN,
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 24,
          paddingTop: 16,
          paddingBottom: 4,
          gap: 8,
        }}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to courses"
          onPress={onBack}
          style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <Ionicons name="arrow-back" size={20} color={F.white} />
          <CCText style={{fontSize: 20, fontWeight: '600', color: F.white}}>
            Courses
          </CCText>
        </Pressable>
      </View>

      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{padding: 24}}>
        <View style={{...COLUMN, gap: 16}}>
        <MasterclassPicker
          busy={ingest.isLoading}
          onBuild={(picked: Masterclass[], chosenTitle: string) =>
            buildFromPicked(picked, chosenTitle)
          }
          onActiveStarted={followActive}
        />

        {pickError ? (
          <CCText
            style={{
              fontSize: 14,
              fontWeight: '600',
              color: F.wrongFg,
            }}>
            {pickError}
          </CCText>
        ) : null}

        <UploadCourseCard
          onStarted={(videoCourseId, note) => {
            setStartedAt(Date.now());
            setUploadNote(note);
            setWatching(videoCourseId);
            rememberWatching(videoCourseId);
          }}
        />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            showAdvanced ? 'Hide the S3 link form' : 'Build from an S3 link instead'
          }
          accessibilityState={{expanded: showAdvanced}}
          onPress={() => setShowAdvanced(value => !value)}
          style={{alignSelf: 'flex-start', paddingVertical: 6}}>
          <CCText style={{fontSize: 14, fontWeight: '600', color: F.white}}>
            {showAdvanced
              ? 'Hide the S3 link form'
              : 'Build from an S3 link instead'}
          </CCText>
        </Pressable>

        {!showAdvanced ? null : (
        <View
          style={[
            {
              backgroundColor: F.white,
              borderRadius: F.radius.card,
              padding: 24,
              gap: 12,
            },
            CARD_SHADOW,
          ]}>
          <CCText style={{fontSize: 22, fontWeight: '600', color: F.brownDark}}>
            Create a course from a recording
          </CCText>
          <CCText style={{fontSize: 15, fontWeight: '500', color: F.brown, lineHeight: 24}}>
            Paste the S3 link to a masterclass recording. The video is split
            into chapters, every position shown on screen is detected, and the
            good ones become puzzles between the video chapters.
          </CCText>

          <TextInput
            value={url}
            onChangeText={setUrl}
            placeholder="https://learn-video-courses.s3.ap-south-1.amazonaws.com/..."
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="Recording link"
            style={{
              borderWidth: 1,
              borderColor: 'rgba(38,35,34,0.2)',
              borderRadius: 8,
              padding: 12,
              fontSize: 14,
            }}
          />

          <View style={{flexDirection: 'row', gap: 12, alignItems: 'center'}}>
            <CourseButton
              label={create.isLoading ? 'Starting…' : 'Generate course'}
              onPress={submit}
              disabled={!url.trim() || create.isLoading}
            />
            {serverError ? (
              <CCText style={{fontSize: 14, fontWeight: '600', color: F.wrongFg, flex: 1}}>
                {serverError}
              </CCText>
            ) : null}
          </View>
        </View>
        )}

        {current ? (
          <View
            style={[
              {
                backgroundColor: F.white,
                borderRadius: F.radius.card,
                padding: 24,
                gap: 10,
                },
              CARD_SHADOW,
            ]}>
            <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
              {current.title || `Recording ${current.video_course_id}`}
            </CCText>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
              {/* A static "Working." paragraph that does not change for forty
                  minutes is indistinguishable from a worker that has died. */}
              {current.status === 'pending' || current.status === 'running' ? (
                <ActivityIndicator color={F.teal} />
              ) : null}
              <CCText style={{fontSize: 14, fontWeight: '600', color: F.brown}}>
                {`Status: ${current.status}`}
                {elapsedLabel ? ` · ${elapsedLabel}` : ''}
              </CCText>
            </View>
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 22}}>
              {current.status === 'running' && isActiveRun
                ? ACTIVE_RUNNING_COPY
                : STATUS_COPY[current.status] ?? ''}
            </CCText>

            {uploadNote && running ? (
              <CCText style={{fontSize: 13, fontWeight: '600', color: F.teal, lineHeight: 20}}>
                {uploadNote}
              </CCText>
            ) : null}

            {current.status === 'failed' && current.error ? (
              <View
                style={{
                  backgroundColor: F.wrongBg,
                  borderWidth: 1,
                  borderColor: F.wrongFg,
                  borderRadius: 4,
                  padding: 12,
                }}>
                <CCText style={{fontSize: 13, fontWeight: '600', color: F.wrongFg}}>
                  {current.error}
                </CCText>
              </View>
            ) : null}

            {current.status === 'failed' ? (
              <View style={{alignSelf: 'flex-start'}}>
                {/* force:true, or the server returns the existing failed row
                    rather than starting a fresh run. */}
                <CourseButton
                  label="Retry"
                  variant="review"
                  disabled={create.isLoading || createActive.isLoading}
                  onPress={() => retry(current.video_course_id)}
                />
              </View>
            ) : null}

            {current.status === 'failed' && activeRetryError ? (
              <CCText
                accessibilityLiveRegion="polite"
                style={{fontSize: 14, fontWeight: '600', color: STATUS.bad.fg}}>
                {activeRetryError}
              </CCText>
            ) : null}

            {current.ready && current.uuid ? (
              <View style={{gap: 10}}>
                {current.thumbnail ? (
                  <View style={{width: 320, maxWidth: '100%'}}>
                    <CourseArtwork
                      title={current.title || ''}
                      thumbnail={current.thumbnail}
                      showTitle={false}
                      radius={10}
                    />
                  </View>
                ) : null}
                <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown}}>
                  {`${current.chapter_count} chapters · ${current.puzzle_count} puzzles · ${current.fen_count} positions found`}
                </CCText>
                <View style={{alignSelf: 'flex-start'}}>
                  <CourseButton
                    label="Open course"
                    showArrow
                    onPress={() => onOpen(current.uuid as string)}
                  />
                </View>
              </View>
            ) : null}
          </View>
        ) : null}
        </View>
      </ScrollView>
    </View>
  );
}
